import { ERROR_TYPE } from 'consts';
import { LocalizedResponseError } from '@web-chat-core/toolkit/src/api3/errors';

const normalize = (value?: string): string => (value || '').toUpperCase();

const REJECTION_KEYWORDS = ['REJECT', 'DECLIN', 'JOIN_WAS_DECLINED', 'NOT_IN_WAITING', 'WAITING_USER'] as const;

const isRejectedLike = (value?: string): boolean => {
    const normalized = normalize(value);
    return (
        REJECTION_KEYWORDS.some((kw) => normalized.includes(kw)) ||
        (normalized.includes('WAITING_LIST') && normalized.includes('NOT_FOUND')) ||
        (normalized.includes('ACCESS_REQUEST') && normalized.includes('DENIED'))
    );
};

export const isJoinRejectedError = (error: unknown): boolean => {
    if (error === 'JOIN_REJECTED') {
        return true;
    }

    if (error instanceof LocalizedResponseError) {
        return error.errors.some((item) => isRejectedLike(item.error) || isRejectedLike(item.message));
    }

    if (typeof error === 'string') {
        return isRejectedLike(error);
    }

    if (error instanceof Error) {
        if (isRejectedLike(error.message) || isRejectedLike(error.toString())) {
            return true;
        }
    }

    if (typeof error === 'object' && error !== null) {
        const record = error as Record<string, unknown>;
        const message = typeof record.message === 'string' ? record.message : undefined;
        const code = typeof record.code === 'string' ? record.code : undefined;

        if (isRejectedLike(message) || isRejectedLike(code)) {
            return true;
        }

        const errors = record.errors;
        if (Array.isArray(errors)) {
            return errors.some((item) => {
                if (typeof item !== 'object' || item === null) {
                    return false;
                }
                const entry = item as Record<string, unknown>;
                return (
                    isRejectedLike(typeof entry.error === 'string' ? entry.error : undefined) ||
                    isRejectedLike(typeof entry.message === 'string' ? entry.message : undefined)
                );
            });
        }
    }

    return false;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

export const getStringField = (value: unknown, field: string): string | undefined => {
    if (!isRecord(value)) return undefined;
    const candidate = value[field];
    return typeof candidate === 'string' ? candidate : undefined;
};

const isRoomFullCode = (value?: string): boolean => value === ERROR_TYPE.VCS_ROOM_IS_FULL;
const isRoomFullLike = (value?: string): boolean => normalize(value).includes(ERROR_TYPE.VCS_ROOM_IS_FULL);

const isLockedRoomIsntStartedCode = (value?: string): boolean => {
    const normalized = normalize(value);
    return (
        normalized === ERROR_TYPE.VCS_LOCKED_ROOM_ISNT_STARTED ||
        (normalized.includes('LOCKED') && normalized.includes('ISNT_STARTED'))
    );
};

/** Звонок ещё не начат (ссылка без активного startCall) или протухший waitingListId. */
export const isLockedRoomIsntStartedError = (error: unknown): boolean => {
    if (typeof error === 'string' && isLockedRoomIsntStartedCode(error)) {
        return true;
    }

    if (error instanceof LocalizedResponseError) {
        if (error.hasError(ERROR_TYPE.VCS_LOCKED_ROOM_ISNT_STARTED)) {
            return true;
        }
        return error.errors.some(
            (item) => isLockedRoomIsntStartedCode(item.error) || isLockedRoomIsntStartedCode(item.message),
        );
    }

    if (error instanceof Error) {
        if (isLockedRoomIsntStartedCode(error.message) || isLockedRoomIsntStartedCode(error.toString())) {
            return true;
        }
    }

    const message = getStringField(error, 'message');
    if (isLockedRoomIsntStartedCode(message)) return true;

    const code = getStringField(error, 'code');
    if (isLockedRoomIsntStartedCode(code)) return true;

    if (!isRecord(error)) return false;
    const errors = error.errors;
    if (!Array.isArray(errors)) return false;

    return errors.some((item) => {
        const itemError = getStringField(item, 'error');
        const itemMessage = getStringField(item, 'message');
        return isLockedRoomIsntStartedCode(itemError) || isLockedRoomIsntStartedCode(itemMessage);
    });
};

/** Протухший waitingListId после F5 / отмены заявки — нужен fresh join без id. */
export const isStaleWaitingRoomError = isLockedRoomIsntStartedError;

/** Звонок по ссылке ещё не начат — показываем модалку на prejoin. */
export const isCallNotStartedError = isLockedRoomIsntStartedError;

export const hasRoomIsFullError = (error: unknown): boolean => {
    if (typeof error === 'string' && isRoomFullLike(error)) {
        return true;
    }

    if (error instanceof Error) {
        if (isRoomFullLike(error.message) || isRoomFullLike(error.toString())) {
            return true;
        }
    }

    const message = getStringField(error, 'message');
    if (isRoomFullCode(message) || isRoomFullLike(message)) return true;

    const code = getStringField(error, 'code');
    if (isRoomFullCode(code) || isRoomFullLike(code)) return true;

    if (!isRecord(error)) return false;
    const errors = error.errors;
    if (!Array.isArray(errors)) return false;

    return errors.some((item) => {
        const itemError = getStringField(item, 'error');
        const itemMessage = getStringField(item, 'message');
        return (
            isRoomFullCode(itemError) ||
            isRoomFullCode(itemMessage) ||
            isRoomFullLike(itemError) ||
            isRoomFullLike(itemMessage)
        );
    });
};
